import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Dialog,
  DialogContent,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { Address } from '../myAddress/types';
import { addressService } from '../myAddress/services/addressService';
import AddressModal from '../myAddress/components/AddressModal';
import { Country } from 'country-state-city';
import { Toast } from '../../../components/common/Toast/Toast';

interface MyAddressInfoProps {
  children?: React.ReactNode;
  onAddressSelect?: (address: Address) => void;
}

export const MyAddressInfo: React.FC<MyAddressInfoProps> = ({ onAddressSelect }) => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<Address | undefined>(undefined);
  const [deletingAddressId, setDeletingAddressId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Helper function to get country name from ISO code
  const getCountryName = (isoCode: string): string => {
    const country = Country.getCountryByCode(isoCode);
    return country?.name || isoCode;
  };

  const [toast, setToast] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message: string, severity: 'success' | 'error') =>
    setToast({ open: true, message, severity });
  // Format phone number with spaces (e.g., "0888888888" -> "088 888 8888")
  const formatPhoneNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '');
    console.log("cleaned " + cleaned + "   phone.  " + phone);
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  };

  // Load addresses on component mount
  useEffect(() => {
    loadAddresses();
  }, []);


  const loadAddresses = async () => {
    setLoading(true);
    try {
      const addressesList = await addressService.getAddresses();
      const list = Array.isArray(addressesList.data) ? addressesList.data : [];

      // ถ้ามี address แค่ 1 อัน และยังไม่เป็น default ให้เรียก API set default
      if (list.length === 1 && !list[0].isDefault && list[0].id) {
        try {
          await addressService.setDefaultAddress(list[0].id);
          // อัปเดต state ในฝั่ง frontend ให้สะท้อนว่าเป็น default แล้ว
          list[0] = { ...list[0], isDefault: true };
        } catch (err) {
          console.error('Error setting default address:', err);
        }
      }

      setAddresses(list);
    } catch (error) {
      console.error('Error loading addresses:', error);
      // setNotification({ type: 'error', message: 'Failed to load addresses' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingAddress(undefined);
    setModalOpen(true);
  };

  const handleEditClick = (address: Address) => {
    setEditingAddress(address);
    setModalOpen(true);
  };

  const handleDeleteClick = (addressId: string) => {
    setDeletingAddressId(addressId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAddressId) return;

    const targetId = deletingAddressId;

    // ปิด dialog และอัปเดต UI ทันที
    setDeleteConfirmOpen(false);
    setDeletingAddressId(null);
    setAddresses((prev) => prev.filter((addr) => addr.id !== targetId));

    try {
      const response = await addressService.deleteAddress(targetId);
      if (!response.success) {
        // ถ้าลบไม่สำเร็จ ให้แจ้ง error และรีโหลดรายการใหม่ให้ตรงกับ server
        // setNotification({ type: 'error', message: 'Failed to delete address' });
        showToast('Failed to delete address.', 'error');
        await loadAddresses();
      }else{
        showToast('Address deleted successfully.', 'success');
      }
    } catch (error) {
      console.error('Error deleting address:', error);
      // setNotification({ type: 'error', message: 'Failed to delete address' });
      showToast('Failed to delete address.', 'error');
      await loadAddresses();
    }
  };

  const handleSaveAddress = async (addressData: Omit<Address, 'id'>) => {
    setIsSaving(true);
    try {
      console.log('edit address ' + editingAddress?.id);
      if (editingAddress?.id) {
        // Update existing address
        const response = await addressService.updateAddress(editingAddress.id, addressData);
        console.log('edit address ' + response + "   " + response.success);
        if (response.success) {
          // Reload addresses to get updated data
          showToast('Address updated.', 'success');
          await loadAddresses();
          setModalOpen(false);
          setEditingAddress(undefined);
        } else {
          showToast('Failed to update address.', 'error');
          // setNotification({ type: 'error', message: 'Failed to update address' });
        }
      } else {
        // Create new address
        const response = await addressService.createAddress(addressData);
        if (response.success) {
          // Reload addresses to get updated data
          await loadAddresses();
          showToast('Address updated.', 'success');
          setModalOpen(false);
          setEditingAddress(undefined);
        } else {
          showToast('Failed to add address.', 'error');
          // setNotification({ type: 'error', message: 'Failed to add address' });
        }
      }
    } catch (error) {
      console.error('Error saving address:', error);
      showToast('Failed to save address.', 'error');
      // setNotification({ type: 'error', message: 'Failed to save address' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ mx: 'auto', mt: 4, mr: { xs: 0, md: 50 } }}>
      <Typography variant="h3" align="center" sx={{ mb: 6, fontFamily: 'Nandia' }}>
        My Account
      </Typography>
 
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography sx={{ fontFamily: 'Nandia', fontSize: 18, fontWeight: 600, color: 'text.primary' }}>My Addresses</Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{
                borderColor: '#000',
                color: '#000',
                textTransform: 'none',
                '&:hover': { borderColor: '#000', bgcolor: 'rgba(0, 0, 0, 0.05)' },
              }}
            >
              Add new address
            </Button>
          </Box>
        </div>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Addresses List */}
      {!loading && addresses.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          
        </Box>
      ) : (
        // <Box sx={{ display: 'grid', gap: 2, width: '450px' }}>

        <Box sx={{ display: "grid", gap: 2, width: { xs: '100%', md: 450 } }}>
          {addresses.map((address) => (
            <Card
              key={address.id}
              variant="outlined"
              sx={{
                p: 2,
                borderColor: "#E6E6E6",
                borderRadius: "10px",
                boxShadow: "none",
                bgcolor: "#fff",
              }}
            >
              {/* Header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontFamily: "Montserrat", fontSize: 14, fontWeight: 600, color: "#111" }}>
                  {address.title || "Untitled Address"}
                </Typography>

                {address.isDefault ? (
                  <Typography sx={{ fontFamily: "Montserrat", fontSize: 12, color: "#7A7A7A", fontStyle: "italic" }}>
                    Default Address
                  </Typography>
                ) : (
                  <Box />
                )}
              </Box>

              <Divider sx={{ my: 1.5, borderColor: "#EEE" }} />

              {/* Name + Phone (same row like screenshot) */}
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 3, flexWrap: "wrap" }}>
                <Typography sx={{ fontFamily: "Montserrat", fontSize: 16, fontWeight: 700, color: "#111" }}>
                  {address.firstName} {address.lastName}
                </Typography>

                <Typography sx={{ fontFamily: "Montserrat", fontSize: 14, color: "#6C6C6C" }}>
                  ({address.phoneCode} {formatPhoneNumber(address.phoneNumber)})
                </Typography>
              </Box>

              {/* Country */}
              <Typography sx={{ fontFamily: "Montserrat", fontSize: 14, color: "#6C6C6C", mt: 1 }}>
                {getCountryName(address.country)}
              </Typography>

              {/* Address line */}
              <Typography sx={{ fontFamily: "Montserrat", fontSize: 14, color: "#6C6C6C" }}>
                {[address.address, address.district, address.province, address.postcode]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>

              {/* Actions (icon + text, no background hover) */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                <Button
                  onClick={() => handleEditClick(address)}
                  startIcon={<EditIcon />}
                  disableRipple
                  sx={{
                    color: "#111",
                    textTransform: "none",
                    fontFamily: "Montserrat",
                    fontSize: 14,
                    px: 0.5,
                    minWidth: "auto",
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  Edit
                </Button>

                <Button
                  onClick={() => handleDeleteClick(address.id!)}
                  startIcon={<DeleteIcon />}
                  disableRipple
                  disabled={address.isDefault}
                  sx={{
                    color: address.isDefault ? "#aaa" : "#111",
                    textTransform: "none",
                    fontFamily: "Montserrat",
                    fontSize: 14,
                    px: 0.5,
                    minWidth: "auto",
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  Delete
                </Button>
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* Add/Edit Address Modal */}
      <AddressModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingAddress(undefined);
        }}
        onSave={handleSaveAddress}
        initialData={editingAddress}
        isLoading={isSaving}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            width: 420,
            position: "relative",
          },
        }}
      >
        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
          <IconButton onClick={() => setDeleteConfirmOpen(false)} size="small" sx={{ color: "#111" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ pt: 5, px: 4, pb: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ fontFamily: "Montserrat", fontSize: 16, color: "#111", textAlign: "center" }}>
              Are you sure you want to delete address
            </Typography>

            <Typography sx={{ fontFamily: "Montserrat", fontSize: 16, fontWeight: 700, color: "#111", textAlign: "center" }}>
              {addresses.find((addr) => addr.id === deletingAddressId)?.title || "this address"}
            </Typography>

            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              sx={{
                mt: 2,
                width: 220,
                height: 48,
                backgroundColor: "#111",
                borderRadius: "14px",
                textTransform: "none",
                fontFamily: "Montserrat",
                fontSize: 14,
                fontWeight: 600,
                "&:hover": { backgroundColor: "#111" },
              }}
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default MyAddressInfo;
